import { Button, CopyButton, DropdownMenu, Text } from '@shared/components';
import {
  ActionExecutionEventType,
  CanonicalToolType,
  useToolValidationSchemaInputLazyQuery,
} from '@shared/generated/graphql';
import {
  ActionExecutionEventData,
  RawAgentMessageEventData,
  RawSupervisorMessageEventData,
} from '@shared/types/action-execution';
import { camelCase, startCase } from 'lodash';
import { useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { EditableText } from './EditableText';
import { storedMessageView } from './storedMessageView';

type ToolCall = {
  id?: string;
  name: string;
  args?: Record<string, unknown>;
  type?: string;
};

type Kwargs = {
  content?: string | unknown[];
  tool_calls?: ToolCall[];
  [key: string]: unknown;
};

type SerializedMessage = {
  kwargs: Kwargs;
  [key: string]: unknown;
};

const ALL_TOOL_NAMES = Object.values(CanonicalToolType).map((t) =>
  t.toLowerCase()
);

function isSerializedMessage(data: unknown): data is SerializedMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'kwargs' in data &&
    typeof data.kwargs === 'object' &&
    data.kwargs !== null
  );
}

function isRawMessageEventData(
  data: ActionExecutionEventData
): data is RawAgentMessageEventData | RawSupervisorMessageEventData {
  return (
    data.eventDataType === ActionExecutionEventType.RAW_AGENT_MESSAGE ||
    data.eventDataType === ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE
  );
}

function updateKwargs(
  data: RawAgentMessageEventData | RawSupervisorMessageEventData,
  patch: Partial<Kwargs>
): ActionExecutionEventData {
  if (!isSerializedMessage(data.data)) return data;
  const updatedData = {
    ...data.data,
    kwargs: { ...data.data.kwargs, ...patch },
  };
  return { ...data, data: JSON.parse(JSON.stringify(updatedData)) };
}

function RawToolCallEditor({
  toolCall,
  isEditable,
  onRemove,
  onUpdateArgs,
  onRenameToolCall,
}: {
  toolCall: ToolCall;
  isEditable?: boolean;
  onRemove?: () => void;
  onUpdateArgs: (args: Record<string, unknown>) => void;
  onRenameToolCall: (newToolName: string) => void;
}) {
  const handleEditInput = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        onUpdateArgs(parsed);
      }
    } catch {
      return;
    }
  };

  return (
    <div className="rounded border border-gray-100 p-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <ToolNameDropdown
          toolName={toolCall.name}
          isEditable={isEditable}
          onSelect={onRenameToolCall}
        />
        {isEditable && onRemove && (
          <Button size="xs" variant="secondary" onPress={onRemove}>
            Remove
          </Button>
        )}
      </div>
      <Text type="body-xs" isHeavy>
        Input
      </Text>
      <EditableText
        value={JSON.stringify(toolCall.args ?? {}, null, 2)}
        isEditable={isEditable}
        onEdit={handleEditInput}
      />
    </div>
  );
}

function ToolNameDropdown({
  toolName,
  isEditable,
  onSelect,
}: {
  toolName: string;
  isEditable?: boolean;
  onSelect: (toolName: string) => void;
}) {
  if (!isEditable) {
    return <Text type="body-sm">{startCase(camelCase(toolName))}</Text>;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <button
          type="button"
          className="rounded px-1 text-left hover:bg-gray-100"
        >
          <Text type="body-sm">{startCase(camelCase(toolName))}</Text>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="z-50 max-h-[300px] min-w-[200px] overflow-y-auto rounded-md bg-white p-1 shadow-lg ring-1 ring-black ring-opacity-5"
        align="start"
      >
        {ALL_TOOL_NAMES.map((name) => (
          <DropdownMenu.Item
            key={name}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-100"
            onSelect={() => onSelect(name)}
          >
            <span>{startCase(camelCase(name))}</span>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export function RawMessageEventContents({
  data,
  isEditable,
  onEditEventData,
}: {
  data: ActionExecutionEventData;
  isEditable?: boolean;
  onEditEventData?: (updatedData: ActionExecutionEventData) => void;
}) {
  const [showRawJson, setShowRawJson] = useState(false);
  const [fetchSchema] = useToolValidationSchemaInputLazyQuery();

  if (!isRawMessageEventData(data)) return null;

  const { content, toolCalls } = storedMessageView(data.data);

  const patchToolCalls = (newToolCalls: ToolCall[]) => {
    if (!onEditEventData) return;
    onEditEventData(updateKwargs(data, { tool_calls: newToolCalls }));
  };

  const handleEditContent = (newContent: string) => {
    if (!onEditEventData) return;
    onEditEventData(updateKwargs(data, { content: newContent }));
  };

  const addToolCall = () => {
    patchToolCalls([
      ...toolCalls,
      {
        id: crypto.randomUUID(),
        name: 'get_current_time',
        args: {},
        type: 'tool_call',
      },
    ]);
  };

  const removeToolCall = (index: number) => {
    patchToolCalls(toolCalls.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-2 p-1">
      <Text type="body-xs" isHeavy>
        Content
      </Text>
      <EditableText
        value={content}
        isEditable={isEditable}
        onEdit={handleEditContent}
      />

      <div className="flex items-center justify-between">
        <Text type="body-xs" isHeavy>
          Tool Calls
        </Text>
        {isEditable && (
          <Button size="xs" variant="secondary" onPress={addToolCall}>
            Add Tool
          </Button>
        )}
      </div>

      {toolCalls.map((toolCall, index) => (
        <RawToolCallEditor
          key={toolCall.id ?? `${toolCall.name}-${index}`}
          toolCall={toolCall}
          isEditable={isEditable}
          onRemove={isEditable ? () => removeToolCall(index) : undefined}
          onUpdateArgs={(args) => {
            patchToolCalls(
              toolCalls.map((tc, i) => (i === index ? { ...tc, args } : tc))
            );
          }}
          onRenameToolCall={async (newToolName) => {
            let defaultArgs: Record<string, unknown> = {};
            try {
              const toolType = newToolName.toUpperCase() as CanonicalToolType;
              const { data: schemaData } = await fetchSchema({
                variables: { input: { toolType } },
              });
              if (schemaData?.toolInputValidationSchema?.jsonSchema) {
                defaultArgs = generateDefaultsFromSchema(
                  schemaData.toolInputValidationSchema.jsonSchema
                );
              }
            } catch {
              // Schema fetch failed
            }
            patchToolCalls(
              toolCalls.map((tc, i) =>
                i === index
                  ? { ...tc, name: newToolName, args: defaultArgs }
                  : tc
              )
            );
          }}
        />
      ))}

      <div className="my-2 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between gap-2 p-2">
          <button
            type="button"
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
            onClick={() => setShowRawJson((open) => !open)}
          >
            {showRawJson ? (
              <FaChevronDown size={10} />
            ) : (
              <FaChevronRight size={10} />
            )}
            <Text type="body-xs">Raw JSON</Text>
          </button>
          <CopyButton
            variant="secondary"
            size="xs"
            textToCopy={JSON.stringify(data.data, null, 2)}
            tooltip="Copy raw message JSON"
          />
        </div>
        {showRawJson && (
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all p-2 pt-0">
            <Text type="body-xs">{JSON.stringify(data.data, null, 2)}</Text>
          </pre>
        )}
      </div>
    </div>
  );
}

function generateDefaultsFromSchema(
  jsonSchema: object
): Record<string, unknown> {
  if (
    typeof jsonSchema !== 'object' ||
    jsonSchema === null ||
    !('properties' in jsonSchema)
  ) {
    return {};
  }

  const schema = jsonSchema as {
    properties?: Record<string, { type?: string; default?: unknown }>;
    required?: string[];
  };

  const properties = schema.properties ?? {};
  const requiredFields = schema.required ?? [];
  const defaults: Record<string, unknown> = {};

  for (const [key, prop] of Object.entries(properties)) {
    if (prop.default !== undefined) {
      defaults[key] = prop.default;
    } else if (requiredFields.includes(key)) {
      switch (prop.type) {
        case 'string':
          defaults[key] = '';
          break;
        case 'number':
        case 'integer':
          defaults[key] = 0;
          break;
        case 'boolean':
          defaults[key] = false;
          break;
        case 'array':
          defaults[key] = [];
          break;
        case 'object':
          defaults[key] = {};
          break;
      }
    }
  }

  return defaults;
}
